import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ShieldCheck, Upload, Trash2, FileSearch, CheckCircle2, XCircle, AlertCircle, Loader2, Ban, X, Plus, Pencil, History } from "lucide-react";

// ---- Combobox simples com busca ----
function BuscaSelect({ itens, valor, onSelect, placeholder, testid, disabled }) {
  const [q, setQ] = useState("");
  const [aberto, setAberto] = useState(false);
  const sel = itens.find((i) => i.id === valor);
  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return itens.slice(0, 50);
    return itens.filter((i) => (i.nome || "").toLowerCase().includes(t)).slice(0, 50);
  }, [q, itens]);
  return (
    <div className="relative">
      <Input
        data-testid={testid}
        disabled={disabled}
        value={aberto ? q : sel ? sel.nome : ""}
        placeholder={placeholder}
        onFocus={() => setAberto(true)}
        onChange={(e) => { setQ(e.target.value); setAberto(true); }}
        onBlur={() => setTimeout(() => setAberto(false), 200)}
      />
      {aberto && filtrados.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg">
          {filtrados.map((i) => (
            <button
              key={i.id}
              type="button"
              className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
              onMouseDown={() => { onSelect(i.id); setQ(""); setAberto(false); }}
            >
              {i.nome}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Editor de lista: adicionar / excluir itens (regras/criterios) ----
function TagListEditor({ label, items, onChange, testid, numeric = false, placeholder = "Adicionar..." }) {
  const [novo, setNovo] = useState("");
  const lista = items || [];
  const add = () => {
    const t = novo.trim();
    if (!t) return;
    const v = numeric ? parseInt(t) : t;
    if (numeric && isNaN(v)) return;
    onChange([...lista, v]);
    setNovo("");
  };
  const remove = (i) => onChange(lista.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mt-1 mb-1.5">
        {lista.length === 0 && <span className="text-xs text-slate-400 italic">Nenhum item</span>}
        {lista.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs rounded-full pl-2.5 pr-1 py-1" data-testid={`${testid}-item-${i}`}>
            {String(it)}
            <button type="button" onClick={() => remove(i)} className="rounded-full hover:bg-red-100 p-0.5 text-red-400 hover:text-red-600" data-testid={`${testid}-del-${i}`} title="Excluir regra">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={novo}
          onChange={(e) => setNovo(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="h-8 text-xs"
          data-testid={`${testid}-input`}
        />
        <Button type="button" size="sm" variant="outline" className="h-8 px-2" onClick={add} data-testid={`${testid}-add`}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

const VEREDITO_CFG = {
  APROVADO: { cor: "bg-green-100 text-green-700 border-green-300", icon: CheckCircle2 },
  REPROVADO: { cor: "bg-red-100 text-red-700 border-red-300", icon: XCircle },
  "EM ANALISE": { cor: "bg-amber-100 text-amber-700 border-amber-300", icon: AlertCircle },
  "FORA DO SEGMENTO": { cor: "bg-slate-200 text-slate-700 border-slate-300", icon: Ban },
};
const ST_CFG = {
  OK: "bg-green-100 text-green-700",
  FALHA: "bg-red-100 text-red-700",
  PENDENTE: "bg-amber-100 text-amber-700",
};

export default function MotorCredito() {
  const [bancos, setBancos] = useState([]);
  const [orgaos, setOrgaos] = useState([]);
  const [regras, setRegras] = useState([]);
  const [tab, setTab] = useState("regras");
  const [editando, setEditando] = useState(null);

  useEffect(() => {
    axios.get(`${API}/bancos`).then((r) => setBancos(r.data || [])).catch(() => {});
    axios.get(`${API}/lookup/instituicoes`).then((r) => setOrgaos((r.data?.instituicoes || []).map((s) => ({ id: s, nome: s })))).catch(() => {});
    carregarRegras();
  }, []);

  const carregarRegras = () => {
    axios.get(`${API}/regras-credito`).then((r) => setRegras(r.data || [])).catch(() => {});
  };

  const editarRegra = (regra) => { setEditando(regra); setTab("importar"); };

  return (
    <div className="p-6 space-y-6" data-testid="motor-credito-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-indigo-600 flex items-center justify-center">
          <ShieldCheck className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Motor de Regras de Crédito</h1>
          <p className="text-sm text-slate-500">Cadastre roteiros operacionais (PDF) e analise clientes por Banco + Órgão</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList>
          <TabsTrigger value="regras" data-testid="tab-regras">Regras Cadastradas</TabsTrigger>
          <TabsTrigger value="importar" data-testid="tab-importar">{editando ? "Editar Regra" : "Importar Roteiro (PDF)"}</TabsTrigger>
          <TabsTrigger value="analisar" data-testid="tab-analisar">Analisar Cliente</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="regras">
          <RegrasList regras={regras} onReload={carregarRegras} onEditar={editarRegra} />
        </TabsContent>
        <TabsContent value="importar">
          <ImportarRoteiro
            bancos={bancos}
            orgaos={orgaos}
            regraInicial={editando}
            onSaved={() => { carregarRegras(); setEditando(null); setTab("regras"); }}
            onCancelEdit={() => { setEditando(null); }}
          />
        </TabsContent>
        <TabsContent value="analisar">
          <AnalisarCliente bancos={bancos} orgaos={orgaos} regras={regras} />
        </TabsContent>
        <TabsContent value="historico">
          <HistoricoAnalises ativo={tab === "historico"} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ===================== ABA: REGRAS CADASTRADAS =====================
function RegrasList({ regras, onReload, onEditar }) {
  const excluir = async (id) => {
    if (!window.confirm("Excluir esta regra?")) return;
    try {
      await axios.delete(`${API}/regras-credito/${id}`);
      toast.success("Regra excluída");
      onReload();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };
  if (!regras.length)
    return <Card><CardContent className="py-10 text-center text-slate-400">Nenhuma regra cadastrada. Importe um Roteiro PDF.</CardContent></Card>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
      {regras.map((r) => (
        <Card key={r.id} data-testid={`regra-card-${r.id}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between gap-2">
              <span className="truncate">{r.banco_nome}</span>
              <div className="flex items-center gap-1 shrink-0">
                {onEditar && (
                  <Button size="sm" variant="ghost" onClick={() => onEditar(r)} data-testid={`editar-regra-${r.id}`} title="Editar regra">
                    <Pencil className="h-4 w-4 text-indigo-500" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => excluir(r.id)} data-testid={`excluir-regra-${r.id}`} title="Excluir regra">
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </CardTitle>
            <Badge className="bg-indigo-100 text-indigo-700 w-fit">{r.orgao_nome}</Badge>
          </CardHeader>
          <CardContent className="text-sm space-y-1 text-slate-600">
            {r.regras?.nome_do_produto && <p className="font-medium text-slate-700">{r.regras.nome_do_produto}</p>}
            {r.regras?.convenio && <p className="text-xs text-slate-500">Convênio: {r.regras.convenio}</p>}
            <p><b>Idade:</b> {r.regras?.idade_min}+ até {r.regras?.idade_max_termino_anos}a no término</p>
            <p><b>Parcela:</b> R$ {r.regras?.parcela_minima} a R$ {r.regras?.parcela_maxima}</p>
            <p><b>Prazos:</b> {(r.regras?.prazos_permitidos || []).join(", ") || "-"}</p>
            <p><b>Aceitos:</b> {(r.regras?.publico_alvo || []).join(", ") || "-"}</p>
            <div className="flex flex-wrap gap-1 pt-1">
              {(r.regras?.criterios_obrigatorios || []).length > 0 && (
                <Badge variant="outline" className="text-[10px]">{r.regras.criterios_obrigatorios.length} obrigatórios</Badge>
              )}
              {(r.regras?.criterios_eliminatorios || []).length > 0 && (
                <Badge variant="outline" className="text-[10px] border-red-200 text-red-600">{r.regras.criterios_eliminatorios.length} eliminatórios</Badge>
              )}
            </div>
            <p className="text-xs text-slate-400">PDF: {r.pdf_nome || "-"}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ===================== ABA: IMPORTAR / EDITAR ROTEIRO =====================
const REGRA_VAZIA = {
  nome_do_produto: "", tipo_de_produto: "Emprestimo consignado", convenio: "", banco_financeira_correspondente: "",
  publico_alvo: [], publico_nao_atendido: [], vinculos_aceitos: [], vinculos_nao_aceitos: [], recebe_pela_prefeitura: false,
  idade_min: 18, idade_max_termino_anos: 64, idade_max_termino_meses: 0, idade_max_termino_dias: 0, regra_de_idade: "",
  margem_percentual: 35, qtd_contratos_max: null,
  prazo_minimo: null, prazo_maximo: null, prazos_permitidos: [], parcela_minima: 50, parcela_maxima: 1500, valor_maximo_operacao: "",
  aceita_spc_serasa: true, aceita_acao_judicial: true, aceita_procuracao: true, aceita_curatela: true, aceita_analfabeto: true, aceita_margem_negativa: false,
  documentos_obrigatorios: [], exige_comprovante_residencia: false, exige_comprovante_renda: false, exige_contracheque: false, exige_senha_averbacao: false, exige_conta_contracheque: false,
  forma_de_averbacao: "", portais_necessarios: [], regras_especiais: "",
  criterios_obrigatorios: [], criterios_eliminatorios: [], checklist_padrao: [],
  pontos_nao_identificados: [], observacoes: "",
};

export function ImportarRoteiro({ bancos, orgaos, onSaved, onCancelEdit, fixedBancoId, fixedBancoNome, regraInicial }) {
  const [bancoId, setBancoId] = useState(fixedBancoId || "");
  const [orgaoId, setOrgaoId] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [extraindo, setExtraindo] = useState(false);
  const [regras, setRegras] = useState(null);
  const [pdfNome, setPdfNome] = useState("");
  const editMode = !!regraInicial;

  // Modo edição: carrega a regra salva (banco/orgao fixos + regras prefill)
  useEffect(() => {
    if (regraInicial) {
      setBancoId(regraInicial.banco_id || "");
      setOrgaoId(regraInicial.orgao_id || "");
      setRegras({ ...REGRA_VAZIA, ...(regraInicial.regras || {}) });
      setPdfNome(regraInicial.pdf_nome || "");
      setArquivo(null);
    }
  }, [regraInicial]);

  const extrair = async () => {
    if (!arquivo) { toast.error("Selecione um PDF"); return; }
    setExtraindo(true);
    try {
      const fd = new FormData();
      fd.append("file", arquivo);
      const r = await axios.post(`${API}/regras-credito/extrair`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRegras({ ...REGRA_VAZIA, ...r.data.regras });
      setPdfNome(r.data.pdf_nome);
      toast.success("Regras extraídas! Revise, adicione/exclua e salve.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao extrair");
    } finally {
      setExtraindo(false);
    }
  };

  const salvar = async () => {
    if (!bancoId || !orgaoId) { toast.error("Selecione Banco e Órgão"); return; }
    if (!regras) { toast.error("Extraia o roteiro primeiro"); return; }
    try {
      await axios.post(`${API}/regras-credito`, {
        banco_id: bancoId, banco_nome: fixedBancoNome || bancos.find((b) => b.id === bancoId)?.nome || regraInicial?.banco_nome || "",
        orgao_id: orgaoId, orgao_nome: orgaos.find((o) => o.id === orgaoId)?.nome || regraInicial?.orgao_nome || "",
        pdf_nome: pdfNome, regras,
      });
      toast.success(editMode ? "Regra atualizada com sucesso!" : "Regra cadastrada com sucesso!");
      setRegras(null); setArquivo(null); setPdfNome("");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const cancelar = () => {
    setRegras(null); setArquivo(null); setPdfNome(""); setBancoId(fixedBancoId || ""); setOrgaoId("");
    if (onCancelEdit) onCancelEdit();
  };

  const upd = (k, v) => setRegras((p) => ({ ...p, [k]: v }));

  const SWITCHES = [
    ["recebe_pela_prefeitura", "Recebe pelo órgão"],
    ["exige_comprovante_residencia", "Exige comp. residência"],
    ["exige_comprovante_renda", "Exige comp. renda"],
    ["exige_contracheque", "Exige contracheque"],
    ["exige_senha_averbacao", "Exige senha averbação"],
    ["exige_conta_contracheque", "Exige conta do contracheque"],
    ["aceita_spc_serasa", "Aceita SPC/SERASA (negativado)"],
    ["aceita_acao_judicial", "Aceita ação judicial"],
    ["aceita_procuracao", "Aceita procuração"],
    ["aceita_curatela", "Aceita curatela"],
    ["aceita_analfabeto", "Aceita analfabeto"],
    ["aceita_margem_negativa", "Aceita margem negativa"],
  ];

  return (
    <div className="space-y-4 mt-4">
      {!editMode && (
        <Card>
          <CardHeader><CardTitle className="text-base">1. Banco + Órgão + Arquivo</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>Banco</Label>{fixedBancoId ? <Input value={fixedBancoNome || bancos.find((b) => b.id === fixedBancoId)?.nome || "Banco atual"} disabled data-testid="imp-banco-fixo" /> : <BuscaSelect testid="imp-banco" itens={bancos} valor={bancoId} onSelect={setBancoId} placeholder="Buscar banco..." />}</div>
            <div><Label>Órgão (Segmento)</Label><BuscaSelect testid="imp-orgao" itens={orgaos} valor={orgaoId} onSelect={setOrgaoId} placeholder="Buscar órgão..." /></div>
            <div>
              <Label>Roteiro Operacional (PDF)</Label>
              <Input type="file" accept="application/pdf" data-testid="imp-arquivo" onChange={(e) => setArquivo(e.target.files?.[0] || null)} />
            </div>
            <div className="md:col-span-3">
              <Button onClick={extrair} disabled={extraindo} data-testid="btn-extrair">
                {extraindo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSearch className="h-4 w-4 mr-2" />}
                {extraindo ? "Extraindo com IA..." : "Extrair Regras (IA)"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {editMode && (
        <Card className="border-indigo-200 bg-indigo-50/40">
          <CardContent className="py-3 flex items-center justify-between">
            <p className="text-sm text-indigo-700">
              <b>Editando regra:</b> {regraInicial.banco_nome} — {regraInicial.orgao_nome}
            </p>
            <Button size="sm" variant="ghost" onClick={cancelar} data-testid="btn-cancelar-edicao">Cancelar</Button>
          </CardContent>
        </Card>
      )}

      {regras && (
        <Card data-testid="preview-regras">
          <CardHeader>
            <CardTitle className="text-base">{editMode ? "Revisar e atualizar regras" : "Revisar regras extraídas, adicionar/excluir e salvar"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 text-sm">
            {/* Identificação */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Nome do produto</Label><Input value={regras.nome_do_produto || ""} onChange={(e) => upd("nome_do_produto", e.target.value)} data-testid="reg-nome-produto" /></div>
              <div><Label>Convênio</Label><Input value={regras.convenio || ""} onChange={(e) => upd("convenio", e.target.value)} data-testid="reg-convenio" /></div>
              <div><Label>Tipo de produto</Label><Input value={regras.tipo_de_produto || ""} onChange={(e) => upd("tipo_de_produto", e.target.value)} /></div>
              <div><Label>Banco/Financeira/Correspondente</Label><Input value={regras.banco_financeira_correspondente || ""} onChange={(e) => upd("banco_financeira_correspondente", e.target.value)} /></div>
            </div>

            {/* Público / vínculos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t">
              <TagListEditor testid="reg-publico-alvo" label="Público alvo (aceitos)" items={regras.publico_alvo} onChange={(v) => upd("publico_alvo", v)} />
              <TagListEditor testid="reg-publico-nao" label="Público NÃO atendido" items={regras.publico_nao_atendido} onChange={(v) => upd("publico_nao_atendido", v)} />
              <TagListEditor testid="reg-vinc-aceitos" label="Vínculos aceitos" items={regras.vinculos_aceitos} onChange={(v) => upd("vinculos_aceitos", v)} />
              <TagListEditor testid="reg-vinc-nao" label="Vínculos não aceitos" items={regras.vinculos_nao_aceitos} onChange={(v) => upd("vinculos_nao_aceitos", v)} />
            </div>

            {/* Idade / margem / prazo / parcela */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t">
              <div><Label>Idade mínima</Label><Input type="number" value={regras.idade_min ?? ""} onChange={(e) => upd("idade_min", e.target.value === "" ? null : parseInt(e.target.value))} /></div>
              <div><Label>Idade máx término (anos)</Label><Input type="number" value={regras.idade_max_termino_anos ?? ""} onChange={(e) => upd("idade_max_termino_anos", e.target.value === "" ? null : parseInt(e.target.value))} /></div>
              <div><Label>+ meses</Label><Input type="number" value={regras.idade_max_termino_meses ?? 0} onChange={(e) => upd("idade_max_termino_meses", parseInt(e.target.value) || 0)} /></div>
              <div><Label>+ dias</Label><Input type="number" value={regras.idade_max_termino_dias ?? 0} onChange={(e) => upd("idade_max_termino_dias", parseInt(e.target.value) || 0)} /></div>
              <div><Label>Margem %</Label><Input type="number" value={regras.margem_percentual ?? ""} onChange={(e) => upd("margem_percentual", e.target.value === "" ? null : parseFloat(e.target.value))} /></div>
              <div><Label>Qtd contratos máx</Label><Input type="number" value={regras.qtd_contratos_max ?? ""} onChange={(e) => upd("qtd_contratos_max", e.target.value === "" ? null : parseInt(e.target.value))} /></div>
              <div><Label>Parcela mínima (R$)</Label><Input type="number" value={regras.parcela_minima ?? ""} onChange={(e) => upd("parcela_minima", e.target.value === "" ? null : parseFloat(e.target.value))} /></div>
              <div><Label>Parcela máxima (R$)</Label><Input type="number" value={regras.parcela_maxima ?? ""} onChange={(e) => upd("parcela_maxima", e.target.value === "" ? null : parseFloat(e.target.value))} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TagListEditor testid="reg-prazos" label="Prazos permitidos (meses)" items={regras.prazos_permitidos} onChange={(v) => upd("prazos_permitidos", v)} numeric placeholder="Ex: 96" />
              <div><Label>Valor máximo da operação</Label><Input value={regras.valor_maximo_operacao || ""} onChange={(e) => upd("valor_maximo_operacao", e.target.value)} /></div>
            </div>
            <div><Label>Regra de idade (texto)</Label><Textarea rows={2} value={regras.regra_de_idade || ""} onChange={(e) => upd("regra_de_idade", e.target.value)} /></div>

            {/* Switches */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t">
              {SWITCHES.map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2 text-xs">
                  <Switch checked={!!regras[k]} onCheckedChange={(v) => upd(k, v)} data-testid={`reg-sw-${k}`} /> {lbl}
                </label>
              ))}
            </div>

            {/* Documentos / portais / operacional */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t">
              <TagListEditor testid="reg-docs" label="Documentos obrigatórios" items={regras.documentos_obrigatorios} onChange={(v) => upd("documentos_obrigatorios", v)} />
              <TagListEditor testid="reg-portais" label="Portais necessários" items={regras.portais_necessarios} onChange={(v) => upd("portais_necessarios", v)} />
            </div>
            <div><Label>Forma de averbação</Label><Textarea rows={2} value={regras.forma_de_averbacao || ""} onChange={(e) => upd("forma_de_averbacao", e.target.value)} /></div>
            <div><Label>Regras especiais</Label><Textarea rows={2} value={regras.regras_especiais || ""} onChange={(e) => upd("regras_especiais", e.target.value)} /></div>

            {/* Critérios / checklist */}
            <div className="grid grid-cols-1 gap-4 pt-3 border-t">
              <TagListEditor testid="reg-crit-obrig" label="Critérios obrigatórios (aprovam)" items={regras.criterios_obrigatorios} onChange={(v) => upd("criterios_obrigatorios", v)} placeholder="Adicionar critério obrigatório..." />
              <TagListEditor testid="reg-crit-elim" label="Critérios eliminatórios (reprovam)" items={regras.criterios_eliminatorios} onChange={(v) => upd("criterios_eliminatorios", v)} placeholder="Adicionar critério eliminatório..." />
              <TagListEditor testid="reg-checklist" label="Checklist padrão (operador)" items={regras.checklist_padrao} onChange={(v) => upd("checklist_padrao", v)} placeholder="Adicionar item de checklist..." />
              <TagListEditor testid="reg-pendencias" label="Pontos não identificados no documento" items={regras.pontos_nao_identificados} onChange={(v) => upd("pontos_nao_identificados", v)} />
            </div>
            <div><Label>Observações</Label><Textarea rows={2} value={regras.observacoes || ""} onChange={(e) => upd("observacoes", e.target.value)} /></div>

            <div className="flex gap-2 pt-2 border-t">
              <Button onClick={salvar} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-salvar-regra">
                <Upload className="h-4 w-4 mr-2" /> {editMode ? "Atualizar Regra" : "Salvar Regra (Banco + Órgão)"}
              </Button>
              {editMode && <Button variant="outline" onClick={cancelar}>Cancelar</Button>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===================== ABA: ANALISAR CLIENTE =====================
const CLIENTE_VAZIO = {
  vinculo: "", recebe_pela_prefeitura: true, idade: "", prazo_desejado: "", parcela_desejada: "",
  tem_margem: true, tem_contracheque: true, tem_comprovante_residencia: true, tem_comprovante_renda: true,
  tem_senha: true, tem_conta_contracheque: true, possui_restricao: false, possui_acao_judicial: false, usa_procuracao: false, usa_curatela: false, analfabeto: false,
  orgao: "", observacoes: "",
};

export function AnalisarCliente({ bancos, orgaos, regras, prefill }) {
  const [bancoId, setBancoId] = useState(prefill?.bancoId || "");
  const [orgaoId, setOrgaoId] = useState(prefill?.orgaoId || "");
  const [c, setC] = useState({ ...CLIENTE_VAZIO, ...(prefill?.cliente || {}) });
  const [resultado, setResultado] = useState(null);
  const [analisando, setAnalisando] = useState(false);

  const temRegra = regras.some((r) => r.banco_id === bancoId && r.orgao_id === orgaoId);
  const upd = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const analisar = async () => {
    if (!bancoId || !orgaoId) { toast.error("Selecione Banco e Órgão"); return; }
    setAnalisando(true);
    try {
      const payload = {
        banco_id: bancoId, orgao_id: orgaoId,
        orgao: c.orgao || orgaos.find((o) => o.id === orgaoId)?.nome || "",
        vinculo: c.vinculo, recebe_pela_prefeitura: c.recebe_pela_prefeitura,
        idade: c.idade ? parseInt(c.idade) : null,
        prazo_desejado: c.prazo_desejado ? parseInt(c.prazo_desejado) : null,
        parcela_desejada: c.parcela_desejada ? parseFloat(c.parcela_desejada) : null,
        tem_margem: c.tem_margem, tem_contracheque: c.tem_contracheque,
        tem_comprovante_residencia: c.tem_comprovante_residencia, tem_comprovante_renda: c.tem_comprovante_renda,
        tem_senha: c.tem_senha, tem_conta_contracheque: c.tem_conta_contracheque,
        possui_restricao: c.possui_restricao, possui_acao_judicial: c.possui_acao_judicial,
        usa_procuracao: c.usa_procuracao, usa_curatela: c.usa_curatela, analfabeto: c.analfabeto, observacoes: c.observacoes,
      };
      const r = await axios.post(`${API}/regras-credito/analisar`, payload);
      setResultado(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao analisar");
      setResultado(null);
    } finally {
      setAnalisando(false);
    }
  };

  const vcfg = resultado ? VEREDITO_CFG[resultado.veredito] || VEREDITO_CFG.REPROVADO : null;
  const VIcon = vcfg?.icon;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Dados do cliente</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Banco</Label><BuscaSelect testid="an-banco" itens={bancos} valor={bancoId} onSelect={setBancoId} placeholder="Buscar banco..." /></div>
            <div><Label>Órgão / Segmento</Label><BuscaSelect testid="an-orgao" itens={orgaos} valor={orgaoId} onSelect={setOrgaoId} placeholder="Buscar órgão..." /></div>
          </div>
          {bancoId && orgaoId && !temRegra && (
            <p className="text-xs text-red-600">⚠ Não há regra cadastrada para este Banco + Órgão. Importe o roteiro primeiro.</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vínculo</Label><Input data-testid="an-vinculo" value={c.vinculo} onChange={(e) => upd("vinculo", e.target.value)} placeholder="Ex: Estatutário Efetivo" /></div>
            <div><Label>Idade</Label><Input type="number" data-testid="an-idade" value={c.idade} onChange={(e) => upd("idade", e.target.value)} /></div>
            <div><Label>Prazo desejado (meses)</Label><Input type="number" data-testid="an-prazo" value={c.prazo_desejado} onChange={(e) => upd("prazo_desejado", e.target.value)} /></div>
            <div><Label>Parcela desejada (R$)</Label><Input type="number" data-testid="an-parcela" value={c.parcela_desejada} onChange={(e) => upd("parcela_desejada", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
            {[
              ["recebe_pela_prefeitura", "Recebe pelo órgão"],
              ["tem_margem", "Tem margem"],
              ["tem_contracheque", "Tem contracheque"],
              ["tem_comprovante_residencia", "Comp. residência"],
              ["tem_comprovante_renda", "Comp. renda"],
              ["tem_senha", "Tem senha"],
              ["tem_conta_contracheque", "Tem conta do contracheque"],
              ["possui_restricao", "Possui restrição (SPC/SERASA)"],
              ["possui_acao_judicial", "Possui ação judicial"],
              ["usa_procuracao", "Usa procuração"],
              ["usa_curatela", "Usa curatela"],
              ["analfabeto", "Analfabeto"],
            ].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-2 text-xs">
                <Switch checked={!!c[k]} onCheckedChange={(v) => upd(k, v)} data-testid={`an-${k}`} /> {lbl}
              </label>
            ))}
          </div>
          <Button onClick={analisar} disabled={analisando} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="btn-analisar">
            {analisando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Analisar Crédito
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Resultado</CardTitle></CardHeader>
        <CardContent>
          {!resultado ? (
            <p className="text-slate-400 text-sm py-10 text-center">Preencha os dados e clique em Analisar.</p>
          ) : (
            <div className="space-y-4" data-testid="resultado-analise">
              <div className={`rounded-lg border-2 p-4 flex items-center gap-3 ${vcfg.cor}`}>
                {VIcon && <VIcon className="h-8 w-8" />}
                <div>
                  <p className="text-xl font-bold" data-testid="veredito">{resultado.veredito}</p>
                  <p className="text-xs">{resultado.motivo}</p>
                </div>
              </div>
              {resultado.criterios?.length > 0 && (
                <Table>
                  <TableHeader><TableRow><TableHead>Critério</TableHead><TableHead>Status</TableHead><TableHead>Esperado</TableHead><TableHead>Informado</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {resultado.criterios.map((cr, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs font-medium">{cr.nome}</TableCell>
                        <TableCell><Badge className={`text-xs ${ST_CFG[cr.status] || ""}`}>{cr.status}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-500">{cr.esperado}</TableCell>
                        <TableCell className="text-xs">{String(cr.informado)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {resultado.checklist_padrao?.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs font-semibold text-slate-600 mb-1">Checklist padrão</p>
                  <ul className="list-disc list-inside text-xs text-slate-600 space-y-0.5">
                    {resultado.checklist_padrao.map((it, i) => <li key={i}>{it}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ===================== ABA: HISTORICO DE ANALISES (auditoria) =====================
const VER_BADGE = {
  APROVADO: "bg-green-100 text-green-700",
  REPROVADO: "bg-red-100 text-red-700",
  "EM ANALISE": "bg-amber-100 text-amber-700",
  "FORA DO SEGMENTO": "bg-slate-200 text-slate-700",
};

function HistoricoAnalises({ ativo }) {
  const [stats, setStats] = useState(null);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);

  const carregar = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/regras-credito/historico/stats`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/regras-credito/historico?limit=300`).then((r) => r.data).catch(() => []),
    ]).then(([s, l]) => { setStats(s); setItens(l || []); }).finally(() => setLoading(false));
  };

  useEffect(() => { if (ativo) carregar(); }, [ativo]);

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }); } catch { return iso; } };

  return (
    <div className="space-y-4 mt-4" data-testid="historico-analises">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Taxa de Aprovação", stats ? `${stats.taxa_aprovacao}%` : "-", "bg-indigo-50 text-indigo-700"],
          ["Aprovados", stats?.aprovados ?? "-", "bg-green-50 text-green-700"],
          ["Reprovados", stats?.reprovados ?? "-", "bg-red-50 text-red-700"],
          ["Em Análise", stats?.em_analise ?? "-", "bg-amber-50 text-amber-700"],
          ["Total Análises", stats?.total ?? "-", "bg-slate-50 text-slate-700"],
        ].map(([lbl, val, cor]) => (
          <Card key={lbl} className={cor} data-testid={`stat-${lbl}`}>
            <CardContent className="py-4">
              <p className="text-xs opacity-70">{lbl}</p>
              <p className="text-2xl font-bold">{val}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Histórico de análises</CardTitle>
          <Button size="sm" variant="outline" onClick={carregar} disabled={loading} data-testid="btn-recarregar-historico">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Recarregar"}
          </Button>
        </CardHeader>
        <CardContent>
          {itens.length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">Nenhuma análise registrada ainda. Abra a ficha de um cliente para gerar.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Banco / Órgão</TableHead>
                  <TableHead>Veredito</TableHead>
                  <TableHead>Regra reprovada</TableHead>
                  <TableHead>Contratos aptos</TableHead>
                  <TableHead>Operador</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((it, i) => (
                  <TableRow key={i} data-testid={`hist-row-${i}`}>
                    <TableCell className="text-xs font-medium">{it.cliente_nome}<br /><span className="text-slate-400 font-mono">{it.cpf}</span></TableCell>
                    <TableCell className="text-xs">{it.banco_nome}<br /><span className="text-slate-400">{it.orgao_nome}</span></TableCell>
                    <TableCell><Badge className={`text-xs ${VER_BADGE[it.veredito] || ""}`}>{it.veredito}</Badge></TableCell>
                    <TableCell className="text-xs text-red-600">{(it.regras_reprovadas || []).join(", ") || "-"}</TableCell>
                    <TableCell className="text-xs">{it.contratos_aptos}/{it.total_contratos}</TableCell>
                    <TableCell className="text-xs">{it.operador || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{fmt(it.analisado_em)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
